import React, { useContext } from "react";
import { PanZoom } from "panzoom";
import logger from "logging/logger";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { selectIsChangingRoom } from "store/rooms";
import "./MapNavigation.scss";
import { selectUser } from "modules/auth/redux/selectors";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Fade } from "@remo-co/ui-core/src/components/Fade";
import { Tooltip } from "@remo-co/ui-core/src/components/Tooltip";
import { useI18n } from "i18n";
import { AddCircleOutline } from "@remo-co/ui-core/src/icons/AddCircleOutline";
import { MyLocation } from "@remo-co/ui-core/src/icons/MyLocation";
import { RemoveCircleOutline } from "@remo-co/ui-core/src/icons/RemoveCircleOutline";
import {
  updateMapCurrentState,
  updateMapTargetState,
  updateMapZoom,
} from "modules/mapNavigation/redux/mapNavigationSlice";
import { selectTheaterTemplate } from "modules/theater/selectors";
import { MAP_NAVIGATION_ZOOMING, SVG_ZOOM } from "modules/event";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { isTypeOfIMapTemplate } from "modules/event/template";
import { Actions, trackAction } from "modules/monitoring";
import { selectIsMobile } from "modules/deviceInfo";
import {
  selectCurrentMapNavigation,
  selectTargetMapNavigation,
} from "./redux/selectors";
import { selectIsChatSupportEnabled } from "../event/selectors";
import useMapNavigation from "./hooks/useMapNavigation";
import { useWhereAmI } from "./hooks/useWhereAmI/useWhereAmI";

interface IMapNavigationProps {
  panzoomInstance: PanZoom | null;
}

const MapNavigation = ({ panzoomInstance }: IMapNavigationProps) => {
  const { t } = useI18n(["userLocator"]);
  const { track } = useContext(TRACKING_CONTEXT);
  const user = useSelector(selectUser);
  const { getCoordinatesForZoom, getNextZoomValue } = useMapNavigation();
  const current = useSelector(selectCurrentMapNavigation);
  const { showWhereAmI } = useWhereAmI({
    forceMapLayout: true,
  });
  const target = useSelector(selectTargetMapNavigation);
  const isMobileView = useSelector(selectIsMobile);
  const spaceConfig = useSelector(selectTheaterTemplate);

  const [isZoomDepthReached, setIsZoomDepthReached] = React.useState<
    number | boolean
  >(0);
  const [isHovering, setIsHovering] = React.useState(false);
  const dispatch = useAppDispatch();
  const isSupportEnabled = useSelector(selectIsChatSupportEnabled);

  const zoom = isTypeOfIMapTemplate(spaceConfig) ? spaceConfig.zoom : SVG_ZOOM;
  const isRoomChangeInProgress = useSelector(selectIsChangingRoom);

  React.useEffect(() => {
    if (current && current.zoom) {
      if (current.zoom === zoom.max || +current.zoom + 0.01 > zoom.max) {
        // Allow 0.01 difference
        setIsZoomDepthReached(MAP_NAVIGATION_ZOOMING.IN);
      } else if (current.zoom === zoom.min || +current.zoom - 0.01 < zoom.min) {
        setIsZoomDepthReached(MAP_NAVIGATION_ZOOMING.OUT);
      } else {
        setIsZoomDepthReached(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.zoom]);

  // Pan & Zoom map depending for locating users
  React.useEffect(() => {
    if (
      !panzoomInstance ||
      (target.x === null && target.y === null && target.zoom === null)
    ) {
      return;
    }

    try {
      // Zoom
      if (target.zoom !== null) {
        if (target.x !== null && target.y !== null) {
          // Pan & Zoom? Pre-run: Hack needed to ensure not being out of bounds
          panzoomInstance.moveTo(0, 0);
          panzoomInstance.zoomTo(0, 0, target.zoom);
        }
        const zoomData = {
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
          zoom: target.zoom,
        };

        panzoomInstance.zoomAbs(zoomData.x, zoomData.y, zoomData.zoom);
      }

      // Pan
      if (target.x !== null && target.y !== null) {
        panzoomInstance.moveTo(target.x, target.y);
      }

      // Clear target values
      dispatch(
        updateMapTargetState({
          x: null,
          y: null,
          zoom: null,
          focus: null,
          highlight: null,
        }),
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      logger.error(
        `[MapNavigation][panZoom] Error with panZoom in navigating map: ${errorMessage}`,
        error instanceof Object ? error : undefined,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.x, target.y, target.zoom, panzoomInstance]);

  const initialize = () => {
    if (!panzoomInstance) {
      return;
    }

    panzoomInstance.on("panend", () => {
      const transform = panzoomInstance.getTransform();

      dispatch(
        updateMapCurrentState({
          x: transform.x,
          y: transform.y,
          zoom: transform.scale,
        }),
      );
    });

    panzoomInstance.on("zoom", () => {
      const transform = panzoomInstance.getTransform();

      dispatch(
        updateMapCurrentState({
          x: transform.x,
          y: transform.y,
          zoom: transform.scale,
        }),
      );
    });
  };

  React.useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isMobileView || !panzoomInstance) {
    return null;
  }

  const onZoomingClick = (index: number) => {
    const actionName = index === MAP_NAVIGATION_ZOOMING.IN ? "IN" : "OUT";

    logger.info("[MapNavigation][onZoomingClick] inputs", {
      index,
      actionName,
      panzoomInstance,
    });

    if (!panzoomInstance) return;
    const transformValue = panzoomInstance.getTransform();
    const zoomLimit = index === MAP_NAVIGATION_ZOOMING.IN ? zoom.max : zoom.min;
    const nextZoomValue = getNextZoomValue(index, current.zoom ?? 1, zoomLimit);

    track(
      index === MAP_NAVIGATION_ZOOMING.IN
        ? Events.MAP_NAVIGATION_ZOOM_IN
        : Events.MAP_NAVIGATION_ZOOM_OUT,
      { zoom: nextZoomValue },
    );

    trackAction(
      index === MAP_NAVIGATION_ZOOMING.IN
        ? Actions.MAP_NAVIGATION_ZOOM_IN
        : Actions.MAP_NAVIGATION_ZOOM_OUT,
      { zoom: nextZoomValue },
    );

    const coordinates = getCoordinatesForZoom(
      transformValue,
      current.zoom ?? 1,
      nextZoomValue,
    );

    logger.info(
      `[MapNavigation][onZoomingClick] Zoom ${actionName} coordinates`,
      { coordinates },
    );
    dispatch(updateMapZoom(coordinates));
  };

  const onLocateMeClick = () => {
    logger.info(`[MapNavigation][onLocateMeClick] userId ${user?.id}`);
    track(Events.MAP_NAVIGATION_USER_LOCATED_SELF);
    trackAction(Actions.MAP_NAVIGATION_USER_LOCATED_SELF);

    showWhereAmI();
  };

  const containerClasses = ["map-navigator-container"];

  if (!isSupportEnabled) {
    containerClasses.push("no-support");
  }

  return (
    <div
      className={containerClasses.join(" ")}
      data-testid="mapNavigator"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Tooltip title={t("userLocator:where.am.i") as string} placement="bottom">
        <Box className="where-am-i-btn-container">
          <Button
            isIconButton
            variant="ghost"
            color="dark"
            data-testid="whereAmILocateMeButton"
            disabled={isRoomChangeInProgress}
            className="where-am-i-btn navigation-btn"
            onClick={onLocateMeClick}
          >
            <MyLocation />
          </Button>
        </Box>
      </Tooltip>

      {/* Zoom In / Out */}
      <Fade in={isHovering} unmountOnExit>
        <Box boxShadow={3} className="zoom-btn-group">
          <Tooltip title={t("userLocator:zoom.in") as string} placement="top">
            {/* Added extra div to retain tooltip even with disabled button */}
            <div>
              <Button
                isIconButton
                variant="ghost"
                color="dark"
                data-testid="whereAmIZoomInButton"
                className="zoom-in-btn navigation-btn remo-hover"
                onClick={() => onZoomingClick(MAP_NAVIGATION_ZOOMING.IN)}
                disabled={isZoomDepthReached === MAP_NAVIGATION_ZOOMING.IN}
              >
                <AddCircleOutline />
              </Button>
            </div>
          </Tooltip>
          <Tooltip title={t("userLocator:zoom.out") as string} placement="top">
            <div>
              <Button
                isIconButton
                variant="ghost"
                color="dark"
                data-testid="whereAmIZoomOutButton"
                className="zoom-out-btn navigation-btn remo-hover"
                onClick={() => onZoomingClick(MAP_NAVIGATION_ZOOMING.OUT)}
                disabled={isZoomDepthReached === MAP_NAVIGATION_ZOOMING.OUT}
              >
                <RemoveCircleOutline />
              </Button>
            </div>
          </Tooltip>
        </Box>
      </Fade>
    </div>
  );
};

export default React.memo(MapNavigation);
